import { useState } from "react";
import SitePhotos from "@/components/SitePhotos";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import FlowNode, { Connector } from "@/components/FlowNode";
import Drawer from "@/components/Drawer";
import ForkManagement from "@/components/ForkManagement";
import { AsBuiltsChip, AsBuiltsForm, asBuiltsDocs } from "@/components/AsBuilts";

const PIT_SATISFIED = ["Payment Confirmed", "Existing Asset"]; // operational condition met; Existing Asset is not a payment
import DocSlot from "@/components/DocSlot";
import DocPreview from "@/components/DocPreview";
import Field from "@/components/Field";
import { fmtAUD, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ExternalCostChip, ExternalCostForm, ExclAmt } from "@/components/ExternalCostCard";
import { Check, Clock3, Circle, GitBranch, CircuitBoard, Construction, Wrench, ShieldCheck, FileCheck2, Send, Building2, PlugZap, House, CheckCircle2, Users, Plus, Pencil, Trash2, ReceiptText } from "lucide-react";

const quoteActive = (c) => !!(c && c.mode === "invoice" && c.quote_required);
const submissionItems = (d) => (d.submission && d.submission.additional_external_costs) || [];
const submissionCostTotal = (d) => {
  const fee = d.submission.connection_fee;
  const hasFee = fee != null && fee !== "";
  const items = submissionItems(d);
  if (!hasFee && items.length === 0) return null;
  return Math.round(((hasFee ? Number(fee) : 0) + items.reduce((s, it) => s + (Number(it.amount) || 0), 0)) * 100) / 100;
};

function AuthorityCostChips({ cost, onOpen }) {
  const c = cost || {};
  const quoted = quoteActive(c);
  return (
    <div className="mt-2 flex flex-col items-start gap-0 xl:absolute xl:left-full xl:top-1/2 xl:mt-0 xl:-translate-y-1/2 xl:flex-row xl:items-center" data-testid="electrical-ext-cost-flow">
      <span className="hidden h-px w-6 shrink-0 bg-gray-300 xl:block" />
      {quoted && (
        <>
          <div className="w-52 shrink-0">
            <ExternalCostChip cost={c} variant="quote" subLabel="Assessment Fee" testId="electrical-ext-quote-chip" onClick={onOpen} />
          </div>
          <span className="ml-6 h-4 w-px shrink-0 bg-gray-300 xl:ml-0 xl:h-px xl:w-6" />
        </>
      )}
      <div className="w-52 shrink-0">
        <ExternalCostChip cost={c} subLabel={quoted ? "Application Cost" : undefined} testId="electrical-ext-cost-chip" onClick={onOpen} />
      </div>
    </div>
  );
}

function SubmissionCostItems({ project, dwelling, docs, refresh, refreshDocs }) {
  const { readOnly } = useAuth();
  const items = submissionItems(dwelling);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [form, setForm] = useState({ description: "", amount: "" });
  const [busy, setBusy] = useState(false);

  const openNew = () => { setForm({ description: "", amount: "" }); setEditing(null); };
  const openEdit = (it) => { setForm({ description: it.description, amount: it.amount }); setEditing(it); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { description: form.description, amount: form.amount === "" ? null : parseFloat(form.amount) };
      if (editing) await api.patch(`/dwellings/${dwelling.id}/submission/external-costs/${editing.id}`, payload);
      else await api.post(`/dwellings/${dwelling.id}/submission/external-costs`, payload);
      toast.success(editing ? "VQR Cost item updated" : `${dwelling.name} — VQR Cost item added`);
      setEditing(undefined);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.description}" (${fmtAUD(it.amount)} Excl. GST)?\n\nIts reference documents and VQR Costs Register row are removed. Roadmap progress is not affected.`)) return;
    try {
      await api.delete(`/dwellings/${dwelling.id}/submission/external-costs/${it.id}`);
      toast.success("External cost item removed");
      refresh(); refreshDocs?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4 rounded-md border border-orange-200 bg-orange-50/40 px-3 py-3" data-testid={`sub-extra-costs-${dwelling.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase text-orange-700" style={{ letterSpacing: "0.12em" }}>Additional VQR Costs</div>
          <p className="mt-0.5 text-[10px] text-gray-500">Administration / reference cost items for this connection (Excl. GST) — each becomes its own VQR Costs Register row; never affects contract financials.</p>
        </div>
        {!readOnly && editing === undefined && (
          <button type="button" data-testid={`sub-extra-cost-add-${dwelling.id}`} className="shrink-0 inline-flex items-center gap-1 rounded-md border border-orange-400 bg-orange-100 px-2.5 py-1.5 text-[10px] font-bold uppercase text-orange-800 transition-colors hover:bg-orange-200" style={{ letterSpacing: "0.06em" }} onClick={openNew}>
            <Plus size={12} strokeWidth={2.2} /> Add VQR Cost
          </button>
        )}
      </div>
      {items.length > 0 && (
        <ul className="mt-3 divide-y divide-orange-100 border-t border-orange-200" data-testid={`sub-extra-cost-list-${dwelling.id}`}>
          {items.map((it) => (
            <li key={it.id} className="py-2.5" data-testid={`sub-extra-cost-item-${it.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 truncate text-sm font-medium text-gray-900" title={it.description}>{it.description}</div>
                <div className="flex shrink-0 items-center gap-1">
                  <span className="font-mono-data text-sm font-semibold text-gray-950" data-testid={`sub-extra-cost-amount-${it.id}`}><ExclAmt v={it.amount} /></span>
                  {!readOnly && (
                    <>
                      <button type="button" className="bdx-icon-btn h-7 w-7" title="Edit item" data-testid={`sub-extra-cost-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil size={13} strokeWidth={1.7} /></button>
                      <button type="button" className="bdx-icon-btn-danger h-7 w-7" title="Remove item" data-testid={`sub-extra-cost-delete-${it.id}`} onClick={() => remove(it)}><Trash2 size={13} strokeWidth={1.7} /></button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-1.5">
                <DocSlot projectId={project.id} linkedTo={`extcost:dwelling_item:${it.id}`} category="External Cost" label="Supporting Documents" docs={docs} onUploaded={refreshDocs} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing !== undefined && (
        <div className="mt-3 rounded-md border border-orange-300 bg-white px-3 py-3" data-testid={`sub-extra-cost-form-${dwelling.id}`}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Description" className="col-span-2"><input data-testid="sub-extra-cost-description" className="bdx-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Distributor Administration Charge" /></Field>
            <Field label="Amount ($ Excl. GST)"><input data-testid="sub-extra-cost-amount-input" type="number" step="0.01" min="0" className="bdx-input font-mono-data" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" className="bdx-btn-secondary" data-testid="sub-extra-cost-cancel" onClick={() => setEditing(undefined)}>Cancel</button>
            <button type="button" className="bdx-btn-primary" data-testid="sub-extra-cost-save" disabled={busy || !form.description.trim() || form.amount === ""} onClick={save}>{busy ? "Saving…" : editing ? "Update Item" : "Add Item"}</button>
          </div>
          {!editing && <p className="mt-2 text-[10px] text-gray-400">Supporting documents can be attached to the item once added.</p>}
        </div>
      )}
    </div>
  );
}

function SaveBar({ onSave, busy }) {
  const { readOnly } = useAuth();
  if (readOnly) return null;
  return (
    <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
      <button data-testid="drawer-save" className="bdx-btn-primary" onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
    </div>
  );
}

/* ---------------- Drawer edit forms ---------------- */

function CesBookingFields({ form, setForm, readOnly, idPrefix, onBook, booked, bookBusy }) {
  return (
    <div className="mb-4 rounded-md border border-sky-200 bg-sky-50/50 px-3 py-3" data-testid={`${idPrefix}-booking-section`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="bdx-label text-sky-800">Inspection Booking (optional — recorded before the CES is completed)</div>
        {booked && <span data-testid={`${idPrefix}-booked-flag`} className="inline-flex shrink-0 items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[9px] font-semibold uppercase text-sky-700" style={{ letterSpacing: "0.07em" }}>Inspection Booked</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="LEI Name"><input data-testid={`${idPrefix}-lei-name`} className="bdx-input" disabled={readOnly} value={form.lei_name} onChange={(e) => setForm({ ...form, lei_name: e.target.value })} /></Field>
        <Field label="EIC Number"><input data-testid={`${idPrefix}-eic-number`} className="bdx-input font-mono-data" disabled={readOnly} value={form.eic_number} onChange={(e) => setForm({ ...form, eic_number: e.target.value })} /></Field>
        <Field label="Date of Booking"><input data-testid={`${idPrefix}-booking-date`} type="date" className="bdx-input" disabled={readOnly} value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} /></Field>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3 border-t border-sky-100 pt-2.5">
        <p className="text-[10px] leading-relaxed text-gray-400">Enter the available booking details and press BOOKED to record the inspection booking. Booking does not complete the CES — completion still uses the fields below.</p>
        {!readOnly && (
          <button type="button" data-testid={`${idPrefix}-book-btn`} disabled={bookBusy}
            className="shrink-0 rounded-md border border-sky-600 bg-sky-600 px-4 py-1.5 text-[11px] font-bold uppercase text-white transition-colors hover:bg-sky-700 disabled:opacity-50" style={{ letterSpacing: "0.08em" }}
            onClick={onBook}>{bookBusy ? "Saving…" : booked ? "Update Booking" : "Booked"}</button>
        )}
      </div>
    </div>
  );
}

function PitForm({ project, docs, refresh, refreshDocs, onClose }) {
  const { readOnly } = useAuth();
  const pit = project.electrical.pit_application;
  const [form, setForm] = useState({ reference: pit.reference || "", date_applied: pit.date_applied || "", payment_expiry: pit.payment_expiry || "", status: pit.status });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/electrical/pit`, form);
      toast.success("Pit application saved");
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Application Reference"><input data-testid="pit-reference" className="bdx-input font-mono-data" disabled={readOnly} value={form.reference} onChange={set("reference")} /></Field>
        <Field label="Status">
          <select data-testid="pit-status" className="bdx-input" disabled={readOnly} value={form.status} onChange={set("status")}>
            <option>Pending</option><option>Payment Confirmed</option><option>Existing Asset</option>
          </select>
        </Field>
        <Field label="Date Applied"><input data-testid="pit-date-applied" type="date" className="bdx-input" disabled={readOnly} value={form.date_applied} onChange={set("date_applied")} /></Field>
        <Field label="Payment Expiry Date"><input data-testid="pit-payment-expiry" type="date" className="bdx-input" disabled={readOnly} value={form.payment_expiry} onChange={set("payment_expiry")} /></Field>
      </div>
      {project.pit_overdue && <p className="text-xs text-red-600 mt-3">Payment expiry date has passed without payment confirmation — this application is overdue.</p>}
      <div className="space-y-2 mt-4">
        <DocSlot projectId={project.id} linkedTo="pit_agreement" category="Distributor Agreement" label="Distributor Agreement Document" docs={docs} onUploaded={refreshDocs} />
        <DocSlot projectId={project.id} linkedTo="pit_design" category="Underground Design Drawing" label="Underground Design Drawing" docs={docs} onUploaded={refreshDocs} />
      </div>
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

function WorksForm({ project, docs, refresh, refreshDocs, onClose }) {
  const { readOnly } = useAuth();
  const [form, setForm] = useState({ ...project.electrical.works });
  const [busy, setBusy] = useState(false);
  const overhead = project.supply_type === "Overhead";

  const items = [
    { key: "group_meter_panel", label: "Group Meter Panel Installed" },
    { key: "underground_electrical", label: "Underground Electrical Completed" },
    { key: "pre_inspection", label: "Pre-Inspection Works Completed" },
  ];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/electrical/works`, form);
      toast.success("Electrical works saved");
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="space-y-2.5">
        {items.map((i) => (
          <label key={i.key} className="flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
            <input data-testid={`works-${i.key}`} type="checkbox" className="w-4 h-4 accent-gray-900" disabled={readOnly}
              checked={!!form[i.key]} onChange={(e) => setForm({ ...form, [i.key]: e.target.checked })} />
            {i.label}
          </label>
        ))}
        {overhead && <p className="text-xs text-gray-400">Underground Electrical is recorded for reference on Overhead supply — it does not gate Electrical Works completion.</p>}
      </div>
      <p className="mt-4 text-[11px] text-gray-400" data-testid="works-docs-moved-note">Electrical Works documents are managed in the <strong>As Builts</strong> container beside this stage.</p>
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

function GmpCesForm({ project, docs, refresh, refreshDocs, onClose }) {
  const { readOnly } = useAuth();
  const g = project.electrical.gmp_ces || {};
  const [form, setForm] = useState({ reference: g.reference || "", date_completed: g.date_completed || "", status: g.status || "Not Started", lei_name: g.lei_name || "", eic_number: g.eic_number || "", booking_date: g.booking_date || "" });
  const [busy, setBusy] = useState(false);
  const [bookBusy, setBookBusy] = useState(false);

  const book = async () => {
    setBookBusy(true);
    try {
      await api.patch(`/projects/${project.id}/electrical/gmp-ces`, {
        lei_name: form.lei_name, eic_number: form.eic_number, booking_date: form.booking_date, booking_confirmed: true });
      toast.success("Group Meter Panel CES — inspection booking recorded");
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBookBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/electrical/gmp-ces`, form);
      toast.success("Group Meter Panel CES saved");
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 mb-3">
        Shared stage — this Certificate of Electrical Safety applies once to the complete project's group meter panel. It is not duplicated per dwelling.
      </p>
      <CesBookingFields form={form} setForm={setForm} readOnly={readOnly} idPrefix="gmp-ces" onBook={book} booked={!!g.booking_confirmed} bookBusy={bookBusy} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="CES Reference Number"><input data-testid="gmp-ces-ref" className="bdx-input font-mono-data" disabled={readOnly} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
        <Field label="Date Completed"><input data-testid="gmp-ces-date" type="date" className="bdx-input" disabled={readOnly} value={form.date_completed} onChange={(e) => setForm({ ...form, date_completed: e.target.value })} /></Field>
        <Field label="Status">
          <select data-testid="gmp-ces-status" className="bdx-input" disabled={readOnly} value={form.status === "Booked" ? "Not Started" : form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Not Started</option><option>Completed</option>
          </select>
        </Field>
      </div>
      <div className="mt-4">
        <DocSlot projectId={project.id} linkedTo="gmp_ces" category="CES Certificate" label="CES PDF Document" docs={docs} onUploaded={refreshDocs} />
      </div>
      <SaveBar onSave={save} busy={busy} />
      <ForkManagement project={project} refresh={refresh} />
    </div>
  );
}

function CesForm({ project, dwelling, docs, refresh, refreshDocs, onClose }) {
  const { readOnly } = useAuth();
  const [form, setForm] = useState({ reference: dwelling.ces.reference || "", date_completed: dwelling.ces.date_completed || "", status: dwelling.ces.status, lei_name: dwelling.ces.lei_name || "", eic_number: dwelling.ces.eic_number || "", booking_date: dwelling.ces.booking_date || "" });
  const [busy, setBusy] = useState(false);
  const [bookBusy, setBookBusy] = useState(false);

  const book = async () => {
    setBookBusy(true);
    try {
      await api.patch(`/dwellings/${dwelling.id}/ces`, {
        lei_name: form.lei_name, eic_number: form.eic_number, booking_date: form.booking_date, booking_confirmed: true });
      toast.success(`${dwelling.name} — CES inspection booking recorded`);
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBookBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dwellings/${dwelling.id}/ces`, form);
      toast.success(`${dwelling.name} — CES saved`);
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <CesBookingFields form={form} setForm={setForm} readOnly={readOnly} idPrefix={`ces-${dwelling.id}`} onBook={book} booked={!!dwelling.ces.booking_confirmed} bookBusy={bookBusy} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="CES Reference Number"><input data-testid={`ces-ref-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
        <Field label="Date Completed"><input data-testid={`ces-date-${dwelling.id}`} type="date" className="bdx-input" disabled={readOnly} value={form.date_completed} onChange={(e) => setForm({ ...form, date_completed: e.target.value })} /></Field>
        <Field label="Completion Status">
          <select data-testid={`ces-status-${dwelling.id}`} className="bdx-input" disabled={readOnly} value={form.status === "Booked" ? "Not Started" : form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Not Started</option><option>Completed</option>
          </select>
        </Field>
      </div>
      <div className="mt-4">
        <DocSlot projectId={project.id} linkedTo={`dwelling_ces:${dwelling.id}`} category="CES Certificate" label="CES Document" docs={docs} onUploaded={refreshDocs} />
      </div>
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

function SubmissionForm({ project, dwelling, refresh, onClose, docs, refreshDocs }) {
  const { readOnly } = useAuth();
  const s = dwelling.submission;
  const [form, setForm] = useState({ type: s.type, date_submitted: s.date_submitted || "", reference: s.reference || "", status: s.status, existing_meter: s.existing_meter || "", existing_nmi: s.existing_nmi || "", connection_fee: s.connection_fee ?? "" });
  const [busy, setBusy] = useState(false);
  const alteration = form.type === "Alteration";

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dwellings/${dwelling.id}/submission`, { ...form, connection_fee: form.connection_fee === "" ? null : parseFloat(form.connection_fee) });
      toast.success(`${dwelling.name} — Distributor submission saved`);
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Connection Type">
          <select data-testid={`sub-type-${dwelling.id}`} className="bdx-input" disabled={readOnly} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option>New Connection</option><option>Alteration</option>
          </select>
        </Field>
        <Field label="Status">
          <select data-testid={`sub-status-${dwelling.id}`} className="bdx-input" disabled={readOnly} value={form.status === "Completed" ? "Submitted" : form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Not Started</option><option>Submitted</option>
          </select>
        </Field>
        <Field label="Connection Reference Number"><input data-testid={`sub-ref-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
        <Field label="Date Submitted"><input data-testid={`sub-date-${dwelling.id}`} type="date" className="bdx-input" disabled={readOnly} value={form.date_submitted} onChange={(e) => setForm({ ...form, date_submitted: e.target.value })} /></Field>
        <Field label="Meter Connection Fee ($ Excl. GST — reference only)">
          <input data-testid={`sub-connection-fee-${dwelling.id}`} type="number" step="0.01" min="0" className="bdx-input font-mono-data" disabled={readOnly} value={form.connection_fee} onChange={(e) => setForm({ ...form, connection_fee: e.target.value })} placeholder="e.g. 1250" />
          <p className="text-[11px] text-gray-400 mt-1">External distributor fee record only — never affects Contract Sum, claims or completion</p>
        </Field>
        {alteration && (
          <>
            <Field label="Existing Meter Number"><input data-testid={`sub-meter-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.existing_meter} onChange={(e) => setForm({ ...form, existing_meter: e.target.value })} /></Field>
            <Field label="Existing NMI Number"><input data-testid={`sub-nmi-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.existing_nmi} onChange={(e) => setForm({ ...form, existing_nmi: e.target.value })} /></Field>
          </>
        )}
      </div>
      <div className="mt-4">
        <DocSlot projectId={project.id} linkedTo={`extcost:dwelling_fee:${dwelling.id}`} category="External Cost" label="Connection Fee Reference Documents" docs={docs} onUploaded={refreshDocs} />
      </div>
      <SubmissionCostItems project={project} dwelling={dwelling} docs={docs} refresh={refresh} refreshDocs={refreshDocs} />
      {submissionCostTotal(dwelling) != null && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-3 py-2" data-testid={`sub-ext-total-${dwelling.id}`}>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase text-orange-700" style={{ letterSpacing: "0.08em" }}><ReceiptText size={11} strokeWidth={1.8} /> Roadmap VQR Cost total</span>
          <span className="font-mono-data text-sm font-semibold text-gray-950"><ExclAmt v={submissionCostTotal(dwelling)} /></span>
        </div>
      )}
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

function RetailerForm({ dwelling, refresh, onClose }) {
  const { readOnly } = useAuth();
  const r = dwelling.retailer;
  const alteration = dwelling.submission.type === "Alteration";
  const initName = alteration
    ? (r.name && r.name !== "Origin Energy" ? r.name : "")
    : (r.name || "Origin Energy");
  const [form, setForm] = useState({ name: initName, contact: r.contact || "", so_number: r.so_number || "", msw_number: r.msw_number || "", nmi: r.nmi || "", date_submitted: r.date_submitted || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dwellings/${dwelling.id}/retailer`, form);
      toast.success(`${dwelling.name} — Retailer saved`);
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={alteration ? "Retailer Name (existing customer's retailer)" : "Retailer Name"}>
          <input data-testid={`ret-name-${dwelling.id}`} className="bdx-input" disabled={readOnly} value={form.name}
            placeholder={alteration ? "Insert Current Retailer" : "Origin Energy"}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          {!alteration && <p className="text-[11px] text-gray-400 mt-1">Default: Origin Energy</p>}
          {alteration && <p className="text-[11px] text-amber-700 mt-1">Alteration — enter the existing/current electrical retailer (do not assume Origin Energy)</p>}
        </Field>
        <Field label="Contact Number"><input data-testid={`ret-contact-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="e.g. 13 24 61" /></Field>
        <Field label="SO Number (optional — sets status to Submitted)"><input data-testid={`ret-so-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.so_number} onChange={(e) => setForm({ ...form, so_number: e.target.value })} /></Field>
        <Field label="MSW Number (optional)"><input data-testid={`ret-msw-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.msw_number} onChange={(e) => setForm({ ...form, msw_number: e.target.value })} /></Field>
        <Field label="NMI Number (optional)"><input data-testid={`ret-nmi-${dwelling.id}`} className="bdx-input font-mono-data" disabled={readOnly} value={form.nmi} onChange={(e) => setForm({ ...form, nmi: e.target.value })} /></Field>
        <Field label="Date Submitted"><input data-testid={`ret-date-${dwelling.id}`} type="date" className="bdx-input" disabled={readOnly} value={form.date_submitted} onChange={(e) => setForm({ ...form, date_submitted: e.target.value })} /></Field>
      </div>
      <p className="text-[11px] text-gray-400 mt-3">Status is set automatically: Awaiting Retailer until an SO Number is saved, then Submitted.</p>
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

function ConnectionForm({ project, dwelling, refresh, onClose }) {
  const { readOnly } = useAuth();
  const c = dwelling.connection;
  const [form, setForm] = useState({ scheduling: c.scheduling, appointment_date: c.appointment_date || "", completed: c.completed, completion_date: c.completion_date || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dwellings/${dwelling.id}/connection`, form);
      toast.success(`${dwelling.name} — Connection saved`);
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Scheduling Status">
          <select data-testid={`conn-scheduling-${dwelling.id}`} className="bdx-input" disabled={readOnly} value={form.scheduling} onChange={(e) => setForm({ ...form, scheduling: e.target.value })}>
            <option>Unscheduled</option><option>Scheduled</option>
          </select>
        </Field>
        <Field label="Truck Appointment Date (optional)"><input data-testid={`conn-appointment-${dwelling.id}`} type="date" className="bdx-input" disabled={readOnly} value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} /></Field>
        <Field label="Connection Completed">
          <label className="flex items-center gap-2 text-sm mt-1.5 cursor-pointer">
            <input data-testid={`conn-completed-${dwelling.id}`} type="checkbox" className="w-4 h-4 accent-emerald-600" disabled={readOnly}
              checked={!!form.completed} onChange={(e) => setForm({ ...form, completed: e.target.checked })} />
            Distributor connection completed
          </label>
        </Field>
        <Field label="Connection Completion Date"><input data-testid={`conn-date-${dwelling.id}`} type="date" className="bdx-input" disabled={readOnly} value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} /></Field>
      </div>
      {dwelling.connection.completed && <p className="text-xs font-bold text-emerald-700 mt-3 uppercase tracking-wider">Connected — this dwelling's connection stream is complete</p>}
      <SaveBar onSave={save} busy={busy} />
    </div>
  );
}

/* ---------------- Roadmap flow ---------------- */

export default function ElectricalTab({ project, docs, refresh, refreshDocs }) {
  const { readOnly } = useAuth();
  const [sel, setSel] = useState(null); // {kind, dwellingId}
  const [preview, setPreview] = useState(null);
  const isSSH = project.project_type === "SSH";

  const underground = project.supply_type === "Underground";
  const pit = project.electrical.pit_application;
  const gmp = project.electrical.gmp_ces || {};
  const stage1Done = underground ? PIT_SATISFIED.includes(pit.status) : true;
  const stage1State = !underground ? "complete" : project.pit_overdue ? "overdue" : stage1Done ? "complete" : "current";
  const worksDone = project.works_complete;
  const worksState = worksDone ? "complete" : stage1Done ? "current" : "future";
  const gmpDone = gmp.status === "Completed";
  const gmpState = gmpDone ? "complete" : worksDone ? "current" : "future";
  const sharedDone = worksDone && gmpDone;
  // GMP CES booking (INSPECTION BOOKED) or completion activates the dwelling CES stages.
  const sharedActive = worksDone && (gmpDone || !!gmp.booking_confirmed);

  const w = project.electrical.works;
  const worksTasks = underground
    ? [w.group_meter_panel, w.underground_electrical, w.pre_inspection]
    : [w.group_meter_panel, w.pre_inspection];
  const worksDoneCount = worksTasks.filter(Boolean).length;

  const att = (keys) => docs.filter((x) => keys.includes(x.linked_to));

  const retailerDone = (d) => !!(d.retailer.so_number || "").trim() || d.retailer.status === "Submitted";

  const retailerDisplayName = (d) => {
    const alt = d.submission.type === "Alteration";
    const nm = (d.retailer.name || "").trim();
    if (alt) return nm && nm !== "Origin Energy" ? nm : "Insert Current Retailer";
    return nm || "Origin Energy";
  };

  const laneStates = (d) => {
    const overridden = (d.future_by_others || d.existing_connection) && !d.connection.completed;
    const dones = [
      d.ces.status === "Completed",
      ["Submitted", "Completed"].includes(d.submission.status),
      retailerDone(d),
      d.connection.completed,
    ];
    const state = (i) => dones[i] ? "complete" : overridden ? "na" : (sharedActive && dones.slice(0, i).every(Boolean)) ? "current" : "future";
    return { dones, state };
  };

  const streamState = (d) => d.connection.completed ? "connected"
    : d.future_by_others ? "future_by_others"
      : d.existing_connection ? "existing_connection_complete" : "in_progress";

  const setCloseout = async (d, state) => {
    if (state === streamState(d)) return;
    try {
      await api.patch(`/projects/${project.id}/dwellings/${d.id}/closeout`, { state });
      const done = { future_by_others: `${d.name} — closed out: Future Connection By Others`,
        existing_connection_complete: `${d.name} — recorded as Existing Connection Complete`,
        in_progress: `${d.name} — reinstated to In Progress` };
      toast.success(done[state]);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const dwelling = sel?.dwellingId ? project.dwellings.find((d) => d.id === sel.dwellingId) : null;

  const authCost = project.electrical.authority_cost || {};
  const authCostMeta = {
    title: underground ? "Underground Pit Application — VQR Cost" : "Overhead Connection — VQR Cost",
    status: authCost.mode === "existing_asset" ? "Existing Asset" : authCost.mode === "invoice" ? (authCost.paid ? "Paid" : "Unpaid") : "Not Recorded",
    tone: authCost.mode === "existing_asset" || (authCost.mode === "invoice" && authCost.paid) ? "orange" : authCost.mode === "invoice" ? "red" : "grey",
  };

  const drawerMeta = {
    authority_cost: authCostMeta,
    pit: { title: `Underground Pit Application — ${project.distributor}`, status: project.pit_overdue ? "Overdue" : pit.status, tone: project.pit_overdue ? "red" : PIT_SATISFIED.includes(pit.status) ? "green" : "amber" },
    overhead: { title: "Overhead Multi Phase Connection", status: "Ready", tone: "grey" },
    works: { title: "Electrical Works", status: worksDone ? "Completed" : "In Progress", tone: worksDone ? "green" : "amber" },
    as_builts: { title: "As Builts — Shared Documents", status: asBuiltsDocs(docs).length ? `${asBuiltsDocs(docs).length} Attached` : "No Documents", tone: asBuiltsDocs(docs).length ? "green" : "grey" },
    gmp: { title: "Group Meter Panel — Certificate of Electrical Safety", status: gmpDone ? "Completed" : gmp.booking_confirmed ? "Inspection Booked" : "Not Started", tone: gmpDone ? "green" : gmp.booking_confirmed ? "sky" : worksDone ? "amber" : "grey" },
    ces: { title: "Certificate of Electrical Safety", status: dwelling?.ces.status === "Completed" ? "Completed" : dwelling?.ces.booking_confirmed ? "Inspection Booked" : dwelling?.ces.status, tone: dwelling?.ces.status === "Completed" ? "green" : dwelling?.ces.booking_confirmed ? "sky" : "grey" },
    submission: { title: `${project.distributor} — Electrical Works Request`, status: dwelling?.submission.status, tone: ["Submitted", "Completed"].includes(dwelling?.submission.status) ? "green" : "grey" },
    retailer: { title: dwelling ? retailerDisplayName(dwelling) : "Electrical Retailer", status: dwelling && retailerDone(dwelling) ? "Submitted" : "Awaiting Retailer", tone: dwelling && retailerDone(dwelling) ? "green" : "amber" },
    connection: { title: `${project.distributor} — Connection`, status: dwelling?.connection.completed ? "Connected" : dwelling?.connection.scheduling, tone: dwelling?.connection.completed ? "green" : dwelling?.connection.scheduling === "Scheduled" ? "amber" : "grey" },
  };

  const close = () => setSel(null);
  const laneGridClass = project.dwellings.length === 1
    ? "mx-auto max-w-md grid-cols-1"
    : project.dwellings.length === 2
      ? "mx-auto max-w-[940px] grid-cols-1 md:grid-cols-2"
      : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4";

  return (
    <div data-testid="electrical-tab" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
      <div className="h-1 bg-[#765297]" />
      <div className="flex flex-col gap-4 border-b border-gray-200 bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-7">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#d9cde5] bg-[#f3eff7] text-[#604184] shadow-sm"><CircuitBoard size={18} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">Electrical Connection Lifecycle</h2>
            <p className="mt-0.5 text-xs text-gray-500">Shared works progressing into independent dwelling connection streams</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
        <SitePhotos project={project} roadmap="electrical" />
        <div className="flex flex-wrap items-center gap-4 text-[10px] font-semibold uppercase text-gray-500" data-testid="roadmap-legend" style={{ letterSpacing: "0.08em" }}>
          <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-emerald-600" /> Complete</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 size={12} className="text-amber-600" /> Current</span>
          <span className="inline-flex items-center gap-1.5"><Circle size={9} className="text-gray-400" /> Future</span>
        </div>
        </div>
      </div>

      <div className="bdx-roadmap-grid px-5 py-8 sm:px-8 lg:px-10 lg:py-10">
      {/* Shared project stages */}
      <div className="mx-auto w-full max-w-[430px]">
        <div className="mb-4 flex items-center justify-center gap-2 text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.14em" }}>
          <span className="h-px w-10 bg-gray-300" /> Shared Project Stages <span className="h-px w-10 bg-gray-300" />
        </div>
        {underground ? (
          <div className="relative">
          <FlowNode
            testId="node-pit"
            title={`Underground Pit Application — ${project.distributor}`}
            status={project.pit_overdue ? "Overdue" : pit.status}
            tone={project.pit_overdue ? "red" : PIT_SATISFIED.includes(pit.status) ? "green" : "amber"}
            state={stage1State}
            onClick={() => setSel({ kind: "pit" })}
            attachments={att(["pit_agreement", "pit_design"])}
            onViewDoc={setPreview}
            appearance="electrical"
            icon={Construction}
            stageType="Distributor Application"
            lines={[
              pit.reference && `Ref: ${pit.reference}`,
              pit.date_applied && `Applied: ${fmtDate(pit.date_applied)}`,
              pit.payment_expiry && `Payment Expiry: ${fmtDate(pit.payment_expiry)}`,
            ]}
          />
          <AuthorityCostChips cost={project.electrical.authority_cost} onOpen={() => setSel({ kind: "authority_cost" })} />
          </div>
        ) : (
          <div className="relative">
          <FlowNode
            testId="node-overhead"
            title="Overhead Multi Phase Connection"
            status="Ready" tone="grey" state="complete"
            onClick={() => setSel({ kind: "overhead" })}
            lines={["Proceeds directly to Electrical Works"]}
            appearance="electrical"
            icon={PlugZap}
            stageType="Supply Preparation"
          />
          <AuthorityCostChips cost={project.electrical.authority_cost} onOpen={() => setSel({ kind: "authority_cost" })} />
          </div>
        )}
        <Connector active={stage1Done} appearance="electrical" />
        <div className="relative">
        <FlowNode
          testId="node-works"
          title="Electrical Works"
          status={worksDone ? "Completed" : worksDoneCount > 0 ? "In Progress" : "Pending"}
          tone={worksDone ? "green" : "amber"}
          state={worksState}
          onClick={() => setSel({ kind: "works" })}
          lines={[`${worksDoneCount} of ${worksTasks.length} Tasks Complete`]}
          appearance="electrical"
          icon={Wrench}
          stageType="Shared Site Works"
        />
        <AsBuiltsChip docs={docs} side="right" testId="electrical-as-builts-chip" onClick={() => setSel({ kind: "as_builts" })} />
        </div>
        <Connector active={worksDone} tall appearance="electrical" />
      </div>

      {/* Branch split */}
      <div className="relative mx-auto max-w-[1280px]">
        <div className={`mx-auto border-t-2 ${sharedDone ? "border-emerald-500" : "border-gray-300"}`} style={{ maxWidth: `${Math.min(project.dwellings.length, 4) * 20}rem` }} />
        <div className="relative mx-auto -mt-4 mb-5 flex w-fit items-center gap-3 rounded-full border border-[#d9cde5] bg-white px-4 py-2.5 shadow-sm">
          <GitBranch size={15} strokeWidth={1.8} className="text-[#604184]" />
          <div>
            <span className="block text-[9px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>Independent Dwelling Connection Streams</span>
            <div data-testid="dwellings-connected-summary" className="mt-0.5 font-heading text-sm font-bold text-gray-950">
            {project.dwellings_connected} of {project.dwellings_required ?? project.dwellings_total} Dwellings Connected
            </div>
            {(project.dwellings_waived || 0) > 0 && (
              <div data-testid="dwellings-waived-summary" className="mt-0.5 text-[9px] font-semibold uppercase text-sky-700" style={{ letterSpacing: "0.08em" }}>
                {project.dwellings_waived} closed — future connection by others
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Meter Panel CES — administrative stage within the connection-stream section */}
      <div className="mx-auto mb-2 w-full max-w-[430px]">
        <FlowNode
          testId="node-gmp-ces"
          title="Group Meter Panel — Certificate of Electrical Safety"
          status={gmpDone ? "Complete" : gmp.booking_confirmed ? "Inspection Booked" : worksDone ? "Book Inspection" : "Not Started"}
          tone={gmpDone ? "green" : gmp.booking_confirmed ? "sky" : worksDone ? "amber" : "grey"}
          state={gmpState}
          onClick={() => setSel({ kind: "gmp" })}
          attachments={att(["gmp_ces"])}
          onViewDoc={setPreview}
          appearance="electrical"
          icon={ShieldCheck}
          stageType="Shared Certification"
          lines={[
            gmp.status !== "Completed" && gmp.lei_name && `LEI: ${gmp.lei_name}`,
            gmp.status !== "Completed" && gmp.eic_number && `EIC: ${gmp.eic_number}`,
            gmp.status !== "Completed" && gmp.booking_date && `Booked: ${fmtDate(gmp.booking_date)}`,
            gmp.reference && `CES: ${gmp.reference}`,
            gmp.date_completed && `Completed: ${fmtDate(gmp.date_completed)}`,
            "Shared stage — applies once to the complete project",
          ]}
        />
        <Connector active={gmpDone} tall appearance="electrical" />
      </div>

      {/* Dwelling lanes */}
      <div className={`grid gap-6 ${laneGridClass}`}>
        {project.dwellings.map((d) => {
          const { state } = laneStates(d);
          const connected = d.connection.completed;
          const waived = !connected && d.future_by_others;
          const existing = !connected && d.existing_connection;
          const green = connected || existing;
          const chipCls = green ? "border-emerald-200 bg-emerald-50 text-emerald-700" : waived ? "border-sky-200 bg-sky-50 text-sky-700" : "border-amber-200 bg-amber-50 text-amber-700";
          return (
            <div key={d.id} data-testid={`dwelling-stream-${d.name.toLowerCase().replace(/ /g, "-")}`} className={`relative rounded-lg border border-gray-200 bg-white px-4 pb-5 shadow-[0_3px_12px_rgba(16,19,21,0.055)] ${(waived || existing) ? "self-start" : ""}`}>
              <Connector active={sharedDone} appearance="electrical" />
              <div className={`-mx-4 flex min-h-14 items-center justify-between gap-3 border-y border-t-2 px-4 py-2.5 ${green ? "border-emerald-300 border-t-emerald-500 bg-emerald-50/60" : waived ? "border-sky-300 border-t-sky-500 bg-sky-50/60" : "border-gray-200 border-t-[#765297] bg-[#faf9f8]"}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-md border shadow-sm ${green ? "border-emerald-200 bg-white text-emerald-700" : waived ? "border-sky-200 bg-white text-sky-700" : "border-[#d9cde5] bg-[#f3eff7] text-[#604184]"}`}><House size={17} strokeWidth={1.8} /></span>
                  <div>
                    <div className="text-[9px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>Dwelling Stream</div>
                    <div className="mt-0.5 font-heading text-sm font-bold text-gray-950">{d.name}</div>
                  </div>
                </div>
                {connected ? (
                  <span data-testid={`dwelling-stream-status-${d.id}`} className={`rounded-full border px-2 py-1 text-[9px] font-semibold uppercase ${chipCls}`} style={{ letterSpacing: "0.06em" }}>Connected</span>
                ) : isSSH && !readOnly ? (
                  <select data-testid={`dwelling-stream-state-${d.id}`} value={streamState(d)} onChange={(e) => setCloseout(d, e.target.value)}
                    title="Set the operational state of this connection stream"
                    className={`max-w-[220px] cursor-pointer rounded-full border py-1 pl-2 pr-1 text-[9px] font-semibold uppercase ${chipCls}`} style={{ letterSpacing: "0.06em" }}>
                    <option value="in_progress">In Progress</option>
                    <option value="future_by_others">Future Connection By Others</option>
                    <option value="existing_connection_complete">Existing Connection Complete</option>
                  </select>
                ) : (
                  <span data-testid={`dwelling-stream-status-${d.id}`} className={`rounded-full border px-2 py-1 text-[9px] font-semibold uppercase ${chipCls}`} style={{ letterSpacing: "0.06em" }}>{waived ? "Future Connection By Others" : existing ? "Existing Connection Complete" : "In Progress"}</span>
                )}
              </div>
              {(waived || existing) ? (
                <Connector active={existing} appearance="electrical" />
              ) : (
              <>
              <Connector active={sharedDone} appearance="electrical" />
              <FlowNode
                testId={`node-ces-${d.id}`}
                title="Certificate of Electrical Safety"
                status={d.ces.status === "Completed" ? "Complete" : d.ces.booking_confirmed ? "Inspection Booked" : state(0) === "current" ? "Book Inspection" : "Not Started"}
                tone={d.ces.status === "Completed" ? "green" : d.ces.booking_confirmed ? "sky" : state(0) === "current" ? "amber" : "grey"}
                state={state(0)}
                onClick={() => setSel({ kind: "ces", dwellingId: d.id })}
                attachments={att([`dwelling_ces:${d.id}`])}
                onViewDoc={setPreview}
                appearance="electrical"
                icon={FileCheck2}
                stageType="Dwelling Certification"
                lines={[
                  d.ces.status !== "Completed" && d.ces.lei_name && `LEI: ${d.ces.lei_name}`,
                  d.ces.status !== "Completed" && d.ces.eic_number && `EIC: ${d.ces.eic_number}`,
                  d.ces.status !== "Completed" && d.ces.booking_date && `Booked: ${fmtDate(d.ces.booking_date)}`,
                  d.ces.reference && `CES: ${d.ces.reference}`,
                  d.ces.date_completed && `Completed: ${fmtDate(d.ces.date_completed)}`,
                ]}
              />
              <Connector active={d.ces.status === "Completed"} appearance="electrical" />
              <FlowNode
                testId={`node-submission-${d.id}`}
                title={`${project.distributor} — Electrical Works Request`}
                status={d.submission.status === "Completed" ? "Complete" : d.submission.status === "Submitted" ? "Submitted" : state(1) === "current" ? "Pending" : "Not Started"}
                tone={["Submitted", "Completed"].includes(d.submission.status) ? "green" : state(1) === "current" ? "amber" : "grey"}
                state={state(1)}
                onClick={() => setSel({ kind: "submission", dwellingId: d.id })}
                appearance="electrical"
                icon={Send}
                stageType="Distributor Submission"
                costTile={submissionCostTotal(d) != null ? <ExclAmt v={submissionCostTotal(d)} /> : null}
                lines={[
                  d.submission.type,
                  d.submission.reference && `Ref: ${d.submission.reference}`,
                  d.submission.date_submitted && `Submitted: ${fmtDate(d.submission.date_submitted)}`,
                  d.submission.connection_fee != null && d.submission.connection_fee !== "" && <span key="fee">Connection Fee: <ExclAmt v={d.submission.connection_fee} /></span>,
                  submissionItems(d).length > 0 && `${submissionItems(d).length} additional VQR Cost item${submissionItems(d).length > 1 ? "s" : ""}`,
                  d.submission.type === "Alteration" && d.submission.existing_meter && `Meter: ${d.submission.existing_meter}`,
                  d.submission.type === "Alteration" && d.submission.existing_nmi && `NMI: ${d.submission.existing_nmi}`,
                ]}
              />
              <Connector active={["Submitted", "Completed"].includes(d.submission.status)} appearance="electrical" />
              <FlowNode
                testId={`node-retailer-${d.id}`}
                title={retailerDisplayName(d)}
                status={retailerDone(d) ? "Submitted" : state(2) === "current" ? "Awaiting Retailer" : "Not Started"}
                tone={retailerDone(d) ? "green" : state(2) === "current" ? "amber" : "grey"}
                state={state(2)}
                onClick={() => setSel({ kind: "retailer", dwellingId: d.id })}
                appearance="electrical"
                icon={Building2}
                stageType="Electrical Retailer"
                lines={[
                  d.retailer.so_number && `SO: ${d.retailer.so_number}`,
                  d.retailer.msw_number && `MSW: ${d.retailer.msw_number}`,
                  d.retailer.nmi && `NMI: ${d.retailer.nmi}`,
                  d.retailer.contact && `Contact: ${d.retailer.contact}`,
                  d.retailer.date_submitted && `Submitted: ${fmtDate(d.retailer.date_submitted)}`,
                ]}
              />
              <Connector active={retailerDone(d)} appearance="electrical" />
              <FlowNode
                testId={`node-connection-${d.id}`}
                title={`${project.distributor} — Connection`}
                status={connected ? "Connected" : d.connection.scheduling}
                tone={connected ? "green" : d.connection.scheduling === "Scheduled" ? "amber" : "grey"}
                state={state(3)}
                onClick={() => setSel({ kind: "connection", dwellingId: d.id })}
                appearance="electrical"
                icon={PlugZap}
                stageType="Final Distributor Stage"
                lines={[
                  d.connection.appointment_date && `Truck: ${fmtDate(d.connection.appointment_date)}`,
                  connected && `Connected: ${fmtDate(d.connection.completion_date)}`,
                ]}
              />
              <Connector active={connected} appearance="electrical" />
              </>
              )}
              <div
                data-testid={`node-connected-${d.id}`}
                className={`flex min-h-12 items-center justify-center gap-2 rounded-md border text-center text-[10px] font-bold uppercase ${
                  connected || existing ? "border-emerald-600 bg-emerald-600 text-white shadow-[0_8px_18px_rgba(5,150,105,0.16)]"
                    : waived ? "border-sky-600 bg-sky-600 text-white shadow-[0_8px_18px_rgba(2,132,199,0.16)]"
                      : "border-dashed border-gray-300 bg-gray-50 text-gray-400"
                }`}
                style={{ letterSpacing: "0.16em" }}
              >
                {connected ? <CheckCircle2 size={15} strokeWidth={2} /> : existing ? <CheckCircle2 size={15} strokeWidth={2} /> : waived ? <Users size={15} strokeWidth={2} /> : <Circle size={10} strokeWidth={1.5} />}
                {existing ? "Connection Completed By Others" : waived ? "Future Connection By Others" : "Connected"}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {/* Node detail drawer */}
      {sel && (
        <Drawer
          title={drawerMeta[sel.kind].title}
          subtitle={dwelling ? `${dwelling.name} — independent connection stream` : `Project ${project.project_number} — ${project.label}`}
          status={drawerMeta[sel.kind].status}
          tone={drawerMeta[sel.kind].tone}
          onClose={close}
        >
          {sel.kind === "authority_cost" && (
            <ExternalCostForm project={project} scope="electrical_authority" cost={project.electrical.authority_cost}
              remitLinkedTo="authority_remittance" docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={close} />
          )}
          {sel.kind === "pit" && <PitForm project={project} docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={close} />}
          {sel.kind === "overhead" && (
            <p className="text-sm text-gray-600">This project uses an overhead multi phase connection — no pit application is required. The roadmap proceeds directly to Electrical Works.</p>
          )}
          {sel.kind === "works" && <WorksForm project={project} docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={close} />}
          {sel.kind === "as_builts" && <AsBuiltsForm project={project} docs={docs} refreshDocs={refreshDocs} />}
          {sel.kind === "gmp" && <GmpCesForm project={project} docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={close} />}
          {sel.kind === "ces" && dwelling && <CesForm project={project} dwelling={dwelling} docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={close} />}
          {sel.kind === "submission" && dwelling && <SubmissionForm project={project} dwelling={dwelling} refresh={refresh} onClose={close} docs={docs} refreshDocs={refreshDocs} />}
          {sel.kind === "retailer" && dwelling && <RetailerForm dwelling={dwelling} refresh={refresh} onClose={close} />}
          {sel.kind === "connection" && dwelling && <ConnectionForm project={project} dwelling={dwelling} refresh={refresh} onClose={close} />}
        </Drawer>
      )}

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
